import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { HiExclamation } from 'react-icons/hi'

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react'

import { tVar } from '@/utils/i18n'

interface GuardTourImpactAlertIProps {
  inProgressGuardTourCount: number
  tourPlansToArchiveCount: number
  tourPlansToUpdateCount: number
}

/**
 * Single callout summarizing what archiving the floor does to guard tours,
 * fed by the previewArchiveFloor query. Follows the app's in-modal Alert
 * pattern (BulkUploadFacilitiesForm, emergency-call ModalAlert): warning
 * (amber) while the impact is informational, error (red) when in-progress
 * tours block the archive outright.
 */
export const GuardTourImpactAlert = ({
  inProgressGuardTourCount,
  tourPlansToArchiveCount,
  tourPlansToUpdateCount,
}: GuardTourImpactAlertIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const isBlocked = inProgressGuardTourCount > 0
  const hasImpact =
    isBlocked || tourPlansToArchiveCount > 0 || tourPlansToUpdateCount > 0

  if (!hasImpact) return null

  return (
    <Alert
      alignItems='flex-start'
      borderRadius='10px'
      color={isBlocked ? `text.alert-danger.${mode}` : `text.warning.${mode}`}
      data-testid='archiveFloorModal_guardTourImpact'
      mt='4'
      status={isBlocked ? 'error' : 'warning'}
    >
      <Icon as={HiExclamation} boxSize='20px' flexShrink={0} mr='10px' />
      <VStack align='start' spacing={1}>
        <AlertTitle fontSize='sm'>
          {t('floors:archive_guard_tour_impact_title')}
        </AlertTitle>
        <AlertDescription>
          <VStack align='start' spacing={1}>
            {isBlocked && (
              <Text
                data-testid='archiveFloorModal_blockedByGuardTours'
                fontSize='sm'
                fontWeight='bold'
              >
                {tVar(t, 'floors:archive_blocked_in_progress_tours', {
                  count: inProgressGuardTourCount,
                })}
              </Text>
            )}
            {tourPlansToUpdateCount > 0 && (
              <Text
                data-testid='archiveFloorModal_tourPlansToUpdate'
                fontSize='sm'
              >
                {tVar(t, 'floors:archive_tour_plans_update', {
                  count: tourPlansToUpdateCount,
                })}
              </Text>
            )}
            {tourPlansToArchiveCount > 0 && (
              <Text
                data-testid='archiveFloorModal_tourPlansToArchive'
                fontSize='sm'
              >
                {tVar(t, 'floors:archive_tour_plans_archive', {
                  count: tourPlansToArchiveCount,
                })}
              </Text>
            )}
          </VStack>
        </AlertDescription>
      </VStack>
    </Alert>
  )
}
